<template>
  <div class="language-selector">
    <!-- Estado: Carregando -->
    <q-btn
      v-if="isLoadingLanguages"
      flat
      size="0.8rem"
      :aria-label="$gettext('LOADING_LANGUAGES')"
      class="btn-header"
      disable
    >
      <q-avatar size="1.5em" class="flag">
        <q-spinner size="1em" color="primary" />
      </q-avatar>
      <span class="text-flag">
        <translate>LOADING</translate>
      </span>
    </q-btn>

    <!-- Estado: Erro -->
    <div v-else-if="hasError" class="error-container">
      <q-btn
        flat
        size="0.8rem"
        :aria-label="$gettext('ERROR_LOADING_LANGUAGE')"
        class="btn-header"
        icon="warning"
        disable
        :label="$gettext('ERROR')"
      />
    </div>

    <!-- Estado: Vazio -->
    <div v-else-if="isEmpty" class="empty-container">
      <q-btn
        flat
        size="0.8rem"
        :aria-label="$gettext('NO_LANGUAGE_AVAILABLE')"
        class="btn-header"
        disable
        :label="$gettext('NO_DATA')"
      />
    </div>

    <!-- Estado: Normal - Seletor de idiomas -->
    <q-btn
      v-else
      id="MainLayoutBtSelectedLanguage"
      flat
      size="0.8rem"
      :aria-label="$gettext('SELECT_LANGUAGE')"
      class="btn-header"
    >
      <q-avatar size="1.5em" class="flag">
        <img class="flag-icon" :src="currentLanguageFlag" />
      </q-avatar>
      <span class="text-flag">{{ currentLanguageLabel }}</span>
      <q-menu
        fit
        anchor="bottom middle"
        self="top middle"
        max-width="350px"
        auto-close
      >
        <q-list style="min-width: 150px">
          <q-item
            v-for="language in layoutController.availableLanguages"
            :key="language.cdIdiomaLocalizacao"
            clickable
            :active="isLanguageSelected(language.cdIdiomaLocalizacao)"
            @click="selectLanguage(language.cdIdiomaLocalizacao)"
          >
            <q-item-section avatar class="flag-options">
              <q-avatar size="1.5em">
                <img
                  class="flag-icon"
                  :src="getFlagImage(language.cdIdiomaLocalizacao)"
                />
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <span class="text-flag-inside">
                {{ getLanguageText(language.cdIdiomaLocalizacao) }}
              </span>
            </q-item-section>
            <q-item-section side>
              <q-icon
                v-if="isLanguageSelected(language.cdIdiomaLocalizacao)"
                name="check"
                color="primary"
              />
            </q-item-section>
          </q-item>
        </q-list>
      </q-menu>
    </q-btn>
  </div>
</template>

<script>
import { Component, Vue } from 'vue-property-decorator'
import { Observer } from 'mobx-vue'
import { LayoutController } from './LayoutController'
import { DEFAULT_LANGUAGE, normalizeLanguageCode } from 'src/shared/utils/languageConstants'

/**
 * Componente para seleção de idioma do usuário
 *
 * Responsabilidades:
 * - Renderizar lista de idiomas disponíveis (carregados do backend)
 * - Indicar o idioma selecionado atualmente
 * - Emitir eventos quando o usuário seleciona um idioma
 * - Tratar estados: carregando, erro, vazio
 *
 * Props: nenhuma (usa controller singleton)
 * Events:
 * - language-selected (idioma selecionado pelo usuario: cdIdiomaLocalizacao)
 * - language-loaded (idioma atual carregado do backend: cdIdiomaLocalizacao)
 */
@Observer
@Component({
  name: 'LanguageSelector'
})
export default class LanguageSelector extends Vue {
  layoutController = LayoutController.getInstance()

  /**
   * Flag images mapping de código para path
   */
  flagImages = {
    pt_BR: require('../../assets/brazil.png'),
    en_US: require('../../assets/USA.png'),
    es_CO: require('../../assets/Colombia.png')
  }

  /**
   * Calcula se está carregando idiomas
   */
  get isLoadingLanguages () {
    return this.layoutController.isLoadingLanguages
  }

  /**
   * Calcula se houve erro no carregamento
   */
  get hasError () {
    return this.layoutController.languagesError !== null
  }

  /**
   * Calcula se a lista está vazia
   */
  get isEmpty () {
    return this.layoutController.isLanguagesEmpty
  }

  /**
   * Flag atual do usuário
   */
  get currentLanguageFlag () {
    const code = normalizeLanguageCode(this.layoutController.currentLanguage) || DEFAULT_LANGUAGE
    return this.flagImages[code] || this.flagImages[DEFAULT_LANGUAGE]
  }

  /**
   * Label atual do idioma selecionado
   */
  get currentLanguageLabel () {
    const currentCode = normalizeLanguageCode(this.layoutController.currentLanguage)
    return currentCode || DEFAULT_LANGUAGE
  }

  /**
   * Obtém a imagem de flag para um idioma
   * @param {string} languageCode - Código do idioma (ex: pt_BR)
   * @returns {string} Path da imagem
   */
  getFlagImage (languageCode) {
    const code = normalizeLanguageCode(languageCode) || DEFAULT_LANGUAGE
    return this.flagImages[code] || this.flagImages[DEFAULT_LANGUAGE]
  }

  /**
   * Texto visível do idioma no seletor
   * @param {string} languageCode - Código bruto do idioma
   * @returns {string}
   */
  getLanguageText (languageCode) {
    return (normalizeLanguageCode(languageCode) || DEFAULT_LANGUAGE).toUpperCase()
  }

  /**
   * Verifica se um idioma está selecionado
   * @param {string} languageCode - Código do idioma
   * @returns {boolean}
   */
  isLanguageSelected (languageCode) {
    return normalizeLanguageCode(this.layoutController.currentLanguage) === normalizeLanguageCode(languageCode)
  }

  /**
   * Seleciona um novo idioma
   * @param {string} languageCode - Código do idioma
   */
  selectLanguage (languageCode) {
    this.$emit('language-selected', normalizeLanguageCode(languageCode) || DEFAULT_LANGUAGE)
  }

  /**
   * Hook de ciclo de vida: carrega dados ao montar
   */
  async mounted () {
    // Carrega idiomas disponíveis
    await this.layoutController.loadAvailableLanguages()
    // Carrega o idioma atual do usuário
    await this.layoutController.loadCurrentLanguage()
    // Backend e a fonte de verdade: avisa o layout para sincronizar gettext, perfil e cache
    this.$emit('language-loaded', this.layoutController.currentLanguage)
  }
}
</script>

<style lang="stylus" scoped>
.language-selector
  display: inline-block

.btn-header
  color: #6f8896

.flag
  margin-right: 5px

.text-flag
  font-size: 0.9rem

.text-flag-inside
  font-size: 0.9rem

.flag-options
  min-width: 36px
  padding-right: 4px

.error-container,
.empty-container
  display: inline-block
</style>
